import {
  Heading,
  Box,
  Button,
  Text,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  HStack,
  Flex,
} from '@chakra-ui/react';
import { FiPlus } from 'react-icons/fi';
import { useState } from 'react';
import { useConfirm } from '@src/hooks/useConfirm';
import { useDeleteEntry, useGetAdminContent } from '@src/modules/settings/context/settings-api-hooks';
import LoadingSpinner from '@src/components/shared/LoadingSpinner';
import EventEdit from './EventEdit';

export default function EventList() {
  const { data: events, isLoading } = useGetAdminContent('coming-soon');
  const { mutate: doDeleteEvent, isLoading: isDeleting } = useDeleteEntry('coming-soon');
  const [editingEvent, setEditingEvent] = useState(null);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { confirm, ConfirmDialog } = useConfirm();
  const hasData = events?.length > 0;
  const [deletingEventId, setDeletingEventId] = useState(null);

  const handleEdit = (event) => {
    setEditingEvent(event);
    onOpen();
  };

  const handleDelete = async (id) => {
    setDeletingEventId(id);
    const confirmed = await confirm({
      title: 'Are you sure you want to delete this event?',
      body: 'This action cannot be undone.',
      confirmButtonLabel: 'Delete Event',
    });

    if (confirmed) {
      await doDeleteEvent(id);
      setDeletingEventId(null);
    }
  };

  const handleClose = () => {
    setEditingEvent(null);
    onClose();
  };

  return (
    <>
      <EventEdit
        isOpen={isOpen}
        onClose={handleClose}
        selectedEvent={editingEvent}
      />
      <ConfirmDialog confirmButtonColor="red" />
      <HStack justifyContent="space-between" mb={4}>
        <Heading as="h2" size="lg">Events</Heading>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="orange"
          onClick={onOpen}
        >
          Add New
        </Button>
      </HStack>

      <Flex
        borderRadius="md"
        bg="white"
        p={4}
        boxShadow="sm"
        flexGrow="1"
      >
        {isLoading && <LoadingSpinner />}
        {!hasData && !isLoading && (
          <Flex
            width="100%"
            alignItems="center"
            justifyContent="center"
          >
            <Text fontSize="lg" color="gray.500">
              No upcoming events found.
            </Text>
          </Flex>
        )}
        {hasData && !isLoading && (
          <TableContainer width="100%">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Date</Th>
                  <Th>Displayed Date</Th>
                  <Th>Title</Th>
                  <Th>Description</Th>
                  <Th>&nbsp;</Th>
                </Tr>
              </Thead>
              <Tbody>
                {events.map((item) => (
                  <Tr key={item.id}>
                    <Td>
                      <Text fontSize="sm" color="gray.700">
                        {new Date(item.releaseDate).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' })}
                      </Text>
                    </Td>
                    <Td>
                      <Text fontSize="sm" color="gray.700">
                        {item.dateFormat === 'MY' ? new Date(item.releaseDate).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long' }) : new Date(item.releaseDate).toLocaleDateString('en-NZ', { year: 'numeric', month: 'long', day: 'numeric' }) }
                      </Text>
                    </Td>
                    <Td>
                      <Text fontWeight="medium">{item.title}</Text>
                    </Td>
                    <Td maxW="400px">
                      <Box
                        as="span"
                        dangerouslySetInnerHTML={{ __html: item.message }}
                        sx={{
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      />
                    </Td>
                    <Td>
                      <HStack spacing={2} justifyContent="flex-end">
                        <Button size="xs" colorScheme="red" onClick={() => handleDelete(item.id)} variant="ghost" isLoading={deletingEventId === item.id && isDeleting}>Delete</Button>
                        <Button size="xs" colorScheme="blue" onClick={() => handleEdit(item)} variant="outline">Edit</Button>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Flex>
    </>
  );
}
